import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { datricApi } from '../../../services/api/datric.api';
import { fetchBalance } from '../credit/credit.slice';
import { Role } from '../../../models/user.model';

export interface AuthState {
	userId?: string | null;
	displayName?: string | null;
	role?: Role | null;
	balance: number;
	// Preço por consulta do próprio usuário — usado com o saldo para exibir
	// quantas consultas ainda cabem.
	pricePerQuery: number;
	// Leilão liberado para este cliente pelo admin. Controla só a exibição da
	// tela — o guard do backend é quem realmente barra.
	leilaoEnabled: boolean;
	liveLeadEnabled: boolean;
	authenticated?: boolean;
	error: boolean;
	loading: boolean;
	waitingAuth: boolean;
}

const initialState: AuthState = {
	userId: localStorage.getItem('userId'),
	displayName: undefined,
	role: undefined,
	balance: 0,
	pricePerQuery: 0,
	leilaoEnabled: false,
	liveLeadEnabled: false,
	authenticated: undefined,
	error: false,
	loading: false,
	waitingAuth: true
};

export const login = createAsyncThunk('auth/login', async (req: { email: string; password: string }, { dispatch }) => {
	try {
		const authResponse = await datricApi.login({ email: req.email, password: req.password });
		axios.defaults.headers.common['Authorization'] = `Bearer ${authResponse.accessToken}`;
		localStorage.setItem('token', authResponse.accessToken);
		localStorage.setItem('userName', authResponse.userName);
		localStorage.setItem('role', authResponse.role);
		localStorage.setItem('balance', String(authResponse.balance));
		dispatch(
			userSetAuth({
				success: true,
				displayName: authResponse.userName,
				role: authResponse.role,
				balance: authResponse.balance,
				pricePerQuery: authResponse.pricePerQuery,
				leilaoEnabled: authResponse.leilaoEnabled,
				liveLeadEnabled: authResponse.liveLeadEnabled
			})
		);
		return true;
	} catch (error) {
		dispatch(userSetAuth({ success: false, error: true }));
		throw error;
	}
});

export const verifyAuth = createAsyncThunk('auth/verify', async (_, { dispatch }) => {
	const token = localStorage.getItem('token');
	if (!token) {
		dispatch(userSetAuth({ success: false }));
		return;
	}
	axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
	try {
		const profile = await datricApi.me();
		if (profile) {
			localStorage.setItem('userId', profile.id);
			localStorage.setItem('userName', profile.name);
			localStorage.setItem('role', profile.role);
			localStorage.setItem('balance', String(profile.balance));
			dispatch(
				userSetAuth({
					success: true,
					userId: profile.id,
					displayName: profile.name,
					role: profile.role,
					balance: profile.balance,
					pricePerQuery: profile.pricePerQuery,
					leilaoEnabled: profile.leilaoEnabled,
					liveLeadEnabled: profile.liveLeadEnabled
				})
			);
		} else {
			dispatch(userSetAuth({ success: false }));
		}
	} catch (e) {
		dispatch(userSetAuth({ success: false }));
	}
});

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		userSetAuth: (state, action) => {
			state.authenticated = action.payload.success;
			state.userId = action.payload.userId ?? state.userId;
			state.displayName = action.payload.displayName ?? state.displayName;
			state.role = (action.payload.role as Role) ?? state.role;
			state.balance = action.payload.balance ?? state.balance;
			state.pricePerQuery = action.payload.pricePerQuery ?? state.pricePerQuery;
			state.leilaoEnabled = action.payload.leilaoEnabled ?? state.leilaoEnabled;
			state.liveLeadEnabled = action.payload.liveLeadEnabled ?? state.liveLeadEnabled;
			state.waitingAuth = false;
			state.error = !!action.payload.error;
		},
		setBalance: (state, action: { payload: number }) => {
			state.balance = action.payload;
			localStorage.setItem('balance', String(action.payload));
		}
	},
	extraReducers: builder => {
		builder.addCase(login.pending, state => {
			state.error = false;
			state.loading = true;
		});
		builder.addCase(login.fulfilled, state => {
			state.loading = false;
		});
		builder.addCase(login.rejected, state => {
			state.error = true;
			state.loading = false;
		});
		// O saldo do navbar vive aqui, mas quem refaz o GET /credit/balance é o
		// slice de credit. Sem este case, o contador só se atualizava no login e
		// no F5 (verifyAuth).
		builder.addCase(fetchBalance.fulfilled, (state, action) => {
			state.balance = action.payload;
			localStorage.setItem('balance', String(action.payload));
		});
	}
});

export const { userSetAuth, setBalance } = authSlice.actions;
